import React, { type CSSProperties } from "react";
import {
  ArrowRight,
  Award,
  Check,
  Clock3,
  ExternalLink,
  Infinity as InfinityIcon,
  MapPin,
  Play,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
  Users,
  Zap,
} from "lucide-react";
import {
  regularHeroIconTokens,
  regularHeroTokens,
  type RegularHeroColorToken,
  type RegularHeroIconToken,
} from "./RegularHero.tokens";

export type RegularHeroLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  target?: string;
  rel?: string;
  "aria-label"?: string;
  [key: string]: any;
}>;

export type RegularHeroImageSource =
  | string
  | {
      src?: string;
      url?: string;
      original?: string;
      thumbnail?: string;
      alt?: string;
      title?: string;
      width?: number;
      height?: number;
      loading?: "eager" | "lazy";
      decorative?: boolean;
      position?: string;
    };

export type RegularHeroImage =
  | string
  | {
      src?: string;
      url?: string;
      original?: string;
      thumbnail?: string;
      alt?: string;
      title?: string;
      width?: number;
      height?: number;
      loading?: "eager" | "lazy";
      decorative?: boolean;
      position?: string;
      desktop?: RegularHeroImageSource;
      tablet?: RegularHeroImageSource;
      mobile?: RegularHeroImageSource;
    };

export type RegularHeroTheme = "light" | "dark";
export type RegularHeroIcon = RegularHeroIconToken | React.ReactNode | false;
export type RegularHeroActionVariant = "solid" | "outline" | "ghost";
export type RegularHeroIconPosition = "start" | "end";

export type RegularHeroAction = {
  label: React.ReactNode;
  href: string;
  mobileLabel?: React.ReactNode;
  description?: React.ReactNode;
  ariaLabel?: string;
  title?: string;
  target?: string;
  rel?: string;
  icon?: RegularHeroIcon;
  iconPosition?: RegularHeroIconPosition;
  variant?: RegularHeroActionVariant;
  outline?: boolean;
};

export type RegularHeroTrustItem = {
  id?: string | number;
  icon?: RegularHeroIcon;
  title?: React.ReactNode;
  label?: React.ReactNode;
  text?: React.ReactNode;
  href?: string;
  ariaLabel?: string;
  target?: string;
  rel?: string;
  stars?: number;
};

export type RegularHeroColors = Partial<Record<RegularHeroColorToken, string>>;

export type RegularHeroProps = {
  Link?: RegularHeroLinkComponent;
  theme?: RegularHeroTheme;
  mode?: RegularHeroTheme;
  image?: RegularHeroImage | false;
  desktopImage?: RegularHeroImageSource;
  tabletImage?: RegularHeroImageSource;
  mobileImage?: RegularHeroImageSource;
  imagePosition?: string;
  tabletImagePosition?: string;
  mobileImagePosition?: string;
  eyebrow?: React.ReactNode;
  eyebrowIcon?: RegularHeroIcon;
  eyebrowVariant?: "plain" | "pill";
  title?: React.ReactNode;
  titleHighlight?: React.ReactNode;
  titleHighlights?: string[];
  titleHighlightPlacement?: "block" | "inline";
  titleComponent?: "h1" | "h2" | "h3";
  lead?: React.ReactNode;
  primaryAction?: RegularHeroAction | false;
  secondaryAction?: RegularHeroAction | false;
  trustItems?: RegularHeroTrustItem[];
  trustCtas?: RegularHeroTrustItem[];
  colors?: RegularHeroColors;
  colours?: RegularHeroColors;
  accentColor?: string;
  accentColour?: string;
  highlightColor?: string;
  highlightColour?: string;
  backgroundColor?: string;
  backgroundColour?: string;
  surfaceColor?: string;
  surfaceColour?: string;
  textColor?: string;
  textColour?: string;
  textSoftColor?: string;
  textSoftColour?: string;
  maxWidth?: number | string;
  minHeight?: number | string;
  mediaAspectRatio?: string;
  id?: string;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
};

type RegularHeroVariant = "canvas" | "split";

type RegularHeroBaseProps = RegularHeroProps & {
  variant: RegularHeroVariant;
};

type CssVarStyle = CSSProperties &
  Record<`--regular-hero-${string}`, string | number | undefined>;

type NormalizedImageSource = {
  src?: string;
  alt?: string;
  title?: string;
  width?: number;
  height?: number;
  loading?: "eager" | "lazy";
  decorative?: boolean;
  position?: string;
};

type ResolvedImage = {
  desktop: NormalizedImageSource;
  tablet?: NormalizedImageSource;
  mobile?: NormalizedImageSource;
  alt: string;
  title?: string;
  width?: number;
  height?: number;
  loading: "eager" | "lazy";
  decorative: boolean;
  desktopPosition: string;
  tabletPosition: string;
  mobilePosition: string;
};

const t = regularHeroTokens;

const iconRegistry: Partial<Record<RegularHeroIconToken, React.ElementType>> = {
  arrowRight: ArrowRight,
  award: Award,
  check: Check,
  clock: Clock3,
  external: ExternalLink,
  mapPin: MapPin,
  meta: InfinityIcon,
  play: Play,
  shield: ShieldCheck,
  sparkles: Sparkles,
  star: Star,
  target: Target,
  users: Users,
  zap: Zap,
};

const regularHeroCss = `
.regular-hero {
  --regular-hero-bg: ${t.colors.lightBackground};
  --regular-hero-surface: ${t.colors.lightSurface};
  --regular-hero-text: ${t.colors.lightText};
  --regular-hero-text-soft: ${t.colors.lightTextSoft};
  --regular-hero-text-muted: ${t.colors.lightTextMuted};
  --regular-hero-border: ${t.colors.lightBorder};
  --regular-hero-overlay: ${t.colors.lightOverlay};
  --regular-hero-accent: ${t.colors.accent};
  --regular-hero-accent-dark: ${t.colors.accentDark};
  --regular-hero-accent-contrast: ${t.colors.accentContrast};
  --regular-hero-highlight: ${t.colors.highlight};
  --regular-hero-shadow: ${t.colors.shadow};
  --regular-hero-max-width: ${t.sizes.maxWidth}px;
  --regular-hero-min-height: ${t.sizes.minHeight}px;
  --regular-hero-media-ratio: ${t.sizes.mediaAspectRatio};
  width: 100%;
  padding: 0;
  background: var(--regular-hero-bg);
  color: var(--regular-hero-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
}
.regular-hero[data-theme="dark"] {
  --regular-hero-bg: ${t.colors.darkBackground};
  --regular-hero-surface: ${t.colors.darkSurface};
  --regular-hero-text: ${t.colors.darkText};
  --regular-hero-text-soft: ${t.colors.darkTextSoft};
  --regular-hero-text-muted: ${t.colors.darkTextMuted};
  --regular-hero-border: ${t.colors.darkBorder};
  --regular-hero-overlay: ${t.colors.darkOverlay};
  --regular-hero-accent: #8f35ff;
  --regular-hero-accent-dark: #6421d8;
  --regular-hero-highlight: #8f35ff;
  --regular-hero-shadow: 0 26px 90px rgba(0, 0, 0, 0.36);
}
.regular-hero,
.regular-hero * {
  box-sizing: border-box;
}
.regular-hero-shell {
  position: relative;
  width: 100%;
  max-width: var(--regular-hero-max-width);
  min-height: var(--regular-hero-min-height);
  margin: 0 auto;
  overflow: hidden;
  background:
    radial-gradient(circle at 86% 88%, color-mix(in srgb, var(--regular-hero-accent) 9%, transparent), transparent 22%),
    var(--regular-hero-bg);
  box-shadow: var(--regular-hero-shadow);
}
.regular-hero[data-frame="true"] .regular-hero-shell {
  border: 1px solid var(--regular-hero-border);
  border-radius: ${t.radius.shell}px;
}
.regular-hero-grid {
  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-columns: minmax(0, 0.98fr) minmax(410px, 0.82fr);
  grid-template-areas: "content media";
  align-items: center;
  min-height: inherit;
  padding: 76px 72px 52px;
}
.regular-hero[data-variant="canvas"] .regular-hero-grid::before,
.regular-hero[data-variant="canvas"] .regular-hero-grid::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
}
.regular-hero[data-variant="canvas"] .regular-hero-grid::before {
  background: var(--regular-hero-overlay);
}
.regular-hero[data-variant="canvas"] .regular-hero-grid::after {
  background: linear-gradient(180deg, transparent 66%, color-mix(in srgb, var(--regular-hero-bg) 56%, transparent) 100%);
}
.regular-hero-content {
  position: relative;
  z-index: 2;
  grid-area: content;
  width: min(100%, 760px);
}
.regular-hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  margin: 0 0 28px;
  color: var(--regular-hero-accent);
  font-size: 16px;
  line-height: 1.16;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}
.regular-hero-eyebrow[data-variant="pill"] {
  padding: 10px 20px;
  border: 1px solid color-mix(in srgb, var(--regular-hero-accent) 76%, transparent);
  border-radius: ${t.radius.pill}px;
  background: color-mix(in srgb, var(--regular-hero-accent) 9%, transparent);
}
.regular-hero-eyebrow-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
}
.regular-hero-eyebrow-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.regular-hero-title {
  max-width: 780px;
  margin: 0;
  color: var(--regular-hero-text);
  font-size: 76px;
  line-height: 1.05;
  font-weight: 900;
  letter-spacing: 0;
  white-space: pre-line;
}
.regular-hero-title-main,
.regular-hero-title-highlight {
  min-width: 0;
  overflow-wrap: anywhere;
}
.regular-hero-title-highlight,
.regular-hero-title-mark {
  color: var(--regular-hero-highlight);
}
.regular-hero-title-highlight[data-placement="block"] {
  display: block;
}
.regular-hero-title-highlight[data-placement="inline"] {
  display: inline;
}
.regular-hero-lead {
  max-width: 660px;
  margin: 28px 0 0;
  color: var(--regular-hero-text-soft);
  font-size: 24px;
  line-height: 1.5;
  font-weight: 500;
  letter-spacing: 0;
}
.regular-hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  align-items: center;
  margin-top: 42px;
}
.regular-hero-action {
  display: inline-grid;
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
  min-height: 62px;
  padding: 0 32px;
  border: 1px solid transparent;
  border-radius: ${t.radius.button}px;
  color: var(--regular-hero-text);
  background: transparent;
  font-size: 18px;
  line-height: 1.12;
  font-weight: 850;
  letter-spacing: 0;
  text-align: center;
  text-decoration: none;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
}
.regular-hero-action:hover,
.regular-hero-action:focus-visible {
  transform: translateY(-1px);
}
.regular-hero-action:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--regular-hero-accent) 26%, transparent);
  outline-offset: 3px;
}
.regular-hero-action[data-variant="solid"] {
  border-color: var(--regular-hero-accent);
  background: var(--regular-hero-accent);
  color: var(--regular-hero-accent-contrast);
  box-shadow: inset 0 -1px 0 color-mix(in srgb, #000000 16%, transparent);
}
.regular-hero-action[data-variant="solid"]:hover,
.regular-hero-action[data-variant="solid"]:focus-visible {
  border-color: var(--regular-hero-accent-dark);
  background: var(--regular-hero-accent-dark);
}
.regular-hero-action[data-variant="outline"] {
  border-color: color-mix(in srgb, var(--regular-hero-accent) 72%, var(--regular-hero-border));
  background: color-mix(in srgb, var(--regular-hero-surface) 72%, transparent);
  color: var(--regular-hero-accent);
  backdrop-filter: blur(12px);
}
.regular-hero[data-theme="dark"] .regular-hero-action[data-variant="outline"] {
  color: var(--regular-hero-text);
}
.regular-hero-action[data-variant="outline"]:hover,
.regular-hero-action[data-variant="outline"]:focus-visible {
  border-color: var(--regular-hero-accent);
  background: color-mix(in srgb, var(--regular-hero-accent) 12%, var(--regular-hero-surface));
}
.regular-hero-action[data-variant="ghost"] {
  border-color: transparent;
  background: transparent;
  color: var(--regular-hero-text);
}
.regular-hero-action-copy {
  display: grid;
  min-width: 0;
  gap: 4px;
  justify-items: start;
}
.regular-hero-action[data-icon-position="end"] .regular-hero-action-copy {
  order: -1;
}
.regular-hero-action-label,
.regular-hero-action-label-mobile,
.regular-hero-action-description {
  min-width: 0;
  overflow-wrap: anywhere;
  color: inherit;
}
.regular-hero-action-label-mobile {
  display: none;
}
.regular-hero-action-description {
  opacity: 0.78;
  font-size: 13px;
  line-height: 1.25;
  font-weight: 650;
}
.regular-hero-action-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
}
.regular-hero-trust {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: center;
  gap: 22px;
  width: min(100%, 900px);
  margin-top: 74px;
}
.regular-hero[data-theme="dark"] .regular-hero-trust {
  gap: 0;
  padding: 14px 16px;
  border: 1px solid color-mix(in srgb, var(--regular-hero-accent) 28%, transparent);
  border-radius: ${t.radius.trust}px;
  background: color-mix(in srgb, var(--regular-hero-surface) 82%, transparent);
  backdrop-filter: blur(14px);
}
.regular-hero-trust-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  min-width: 0;
  min-height: 54px;
  color: inherit;
  text-decoration: none;
}
.regular-hero[data-theme="dark"] .regular-hero-trust-item {
  padding: 0 18px;
  border-left: 1px solid color-mix(in srgb, var(--regular-hero-accent) 22%, transparent);
}
.regular-hero[data-theme="dark"] .regular-hero-trust-item:first-child {
  border-left: 0;
}
.regular-hero-trust-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  color: var(--regular-hero-accent);
}
.regular-hero-trust-copy {
  display: grid;
  min-width: 0;
  gap: 5px;
}
.regular-hero-trust-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}
.regular-hero-trust-title {
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--regular-hero-text);
  font-size: 20px;
  line-height: 1.08;
  font-weight: 900;
  letter-spacing: 0;
}
.regular-hero-trust-text {
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--regular-hero-text-soft);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 650;
  letter-spacing: 0;
}
.regular-hero-stars {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #f8b323;
}
.regular-hero-media-wrap {
  grid-area: media;
  position: relative;
  z-index: 2;
  min-width: 0;
}
.regular-hero[data-variant="canvas"] .regular-hero-media-wrap {
  position: absolute;
  grid-area: 1 / 1 / -1 / -1;
  inset: 0 0 0 34%;
  z-index: 0;
}
.regular-hero[data-variant="split"] .regular-hero-media-wrap {
  align-self: center;
}
.regular-hero-picture {
  display: block;
  width: 100%;
  min-width: 0;
}
.regular-hero-picture img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
  object-position: var(--regular-hero-image-position, center);
}
.regular-hero[data-variant="canvas"] .regular-hero-picture,
.regular-hero[data-variant="canvas"] .regular-hero-picture img {
  height: 100%;
}
.regular-hero[data-variant="canvas"] .regular-hero-picture img {
  object-fit: contain;
  object-position: right center;
}
.regular-hero[data-variant="split"] .regular-hero-picture {
  overflow: hidden;
  border: 1px solid var(--regular-hero-border);
  border-radius: ${t.radius.media}px;
  background: var(--regular-hero-surface);
  box-shadow: 0 24px 60px color-mix(in srgb, #000000 14%, transparent);
}
.regular-hero[data-variant="split"] .regular-hero-picture img {
  aspect-ratio: var(--regular-hero-media-ratio);
}
.regular-hero-google-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  color: #1a73e8;
  font-size: 27px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
}
@media (max-width: 1180px) {
  .regular-hero-grid {
    grid-template-columns: minmax(0, 0.98fr) minmax(340px, 0.82fr);
    padding: 62px 42px 42px;
  }
  .regular-hero-title {
    font-size: 60px;
    line-height: 1.07;
  }
  .regular-hero-lead {
    max-width: 560px;
    font-size: 20px;
  }
  .regular-hero-trust {
    margin-top: 56px;
  }
}
@media (max-width: 860px) {
  .regular-hero-shell {
    min-height: 0;
  }
  .regular-hero-grid {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "content"
      "media";
    row-gap: 30px;
    min-height: 0;
    padding: 42px 34px 34px;
  }
  .regular-hero[data-variant="canvas"] .regular-hero-grid::before {
    background: linear-gradient(180deg, var(--regular-hero-bg) 0%, color-mix(in srgb, var(--regular-hero-bg) 88%, transparent) 52%, color-mix(in srgb, var(--regular-hero-bg) 24%, transparent) 100%);
  }
  .regular-hero[data-variant="canvas"] .regular-hero-grid::after {
    display: none;
  }
  .regular-hero-content {
    width: 100%;
  }
  .regular-hero-eyebrow {
    margin-bottom: 18px;
    font-size: 13px;
  }
  .regular-hero-title {
    max-width: 620px;
    font-size: 48px;
    line-height: 1.08;
  }
  .regular-hero-lead {
    max-width: 620px;
    margin-top: 20px;
    font-size: 17px;
    line-height: 1.56;
  }
  .regular-hero-actions {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
    width: min(100%, 430px);
    margin-top: 28px;
  }
  .regular-hero-action {
    width: 100%;
    min-height: 56px;
    padding: 0 22px;
    font-size: 16px;
  }
  .regular-hero-trust {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
    margin-top: 34px;
  }
  .regular-hero-trust-item {
    align-content: start;
    grid-template-columns: minmax(0, 1fr);
    justify-items: center;
    gap: 8px;
    text-align: center;
  }
  .regular-hero[data-theme="dark"] .regular-hero-trust-item {
    padding: 0 10px;
  }
  .regular-hero-trust-title-row {
    justify-content: center;
  }
  .regular-hero-trust-title {
    font-size: 15px;
  }
  .regular-hero-trust-text {
    font-size: 12px;
  }
  .regular-hero[data-variant="canvas"] .regular-hero-media-wrap {
    grid-area: media;
    position: relative;
    inset: auto;
    z-index: 2;
    width: 100%;
    margin-top: 0;
  }
  .regular-hero[data-variant="canvas"] .regular-hero-picture {
    overflow: hidden;
    height: auto;
    border-radius: ${t.radius.media}px;
  }
  .regular-hero[data-variant="canvas"] .regular-hero-picture img {
    height: auto;
    aspect-ratio: var(--regular-hero-media-ratio);
  }
  .regular-hero-picture img {
    object-position: var(--regular-hero-tablet-image-position, var(--regular-hero-image-position, center));
  }
}
@media (max-width: 520px) {
  .regular-hero-grid {
    padding: 30px 18px 22px;
    row-gap: 22px;
  }
  .regular-hero-eyebrow {
    max-width: 100%;
    font-size: 11px;
  }
  .regular-hero-eyebrow[data-variant="pill"] {
    padding: 8px 12px;
  }
  .regular-hero-title {
    font-size: 38px;
    line-height: 1.09;
  }
  .regular-hero-lead {
    font-size: 16px;
    line-height: 1.55;
  }
  .regular-hero-action {
    min-height: 52px;
    font-size: 15px;
  }
  .regular-hero-action-label-mobile {
    display: inline;
  }
  .regular-hero-action-label-mobile + .regular-hero-action-label {
    display: none;
  }
  .regular-hero-trust {
    grid-template-columns: minmax(0, 1fr);
    margin-top: 28px;
  }
  .regular-hero[data-theme="dark"] .regular-hero-trust {
    gap: 10px;
    padding: 12px;
  }
  .regular-hero[data-theme="dark"] .regular-hero-trust-item {
    border-left: 0;
    border-top: 1px solid color-mix(in srgb, var(--regular-hero-accent) 22%, transparent);
    padding: 10px 0 0;
  }
  .regular-hero[data-theme="dark"] .regular-hero-trust-item:first-child {
    border-top: 0;
    padding-top: 0;
  }
  .regular-hero-trust-item {
    grid-template-columns: auto minmax(0, 1fr);
    justify-items: start;
    text-align: left;
  }
  .regular-hero-trust-title-row {
    justify-content: flex-start;
  }
  .regular-hero-picture img {
    object-position: var(--regular-hero-mobile-image-position, var(--regular-hero-tablet-image-position, var(--regular-hero-image-position, center)));
  }
}
`;

function getCssSize(value: number | string | undefined): string | undefined {
  if (typeof value === "number") {
    return `${value}px`;
  }

  return value;
}

function sanitizeId(value: string | undefined, fallback: string) {
  const source = value || fallback;

  return source
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-_]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizeLineBreaks(value: string) {
  return value.replace(/\\n/g, "\n").replace(/\r\n|\r/g, "\n");
}

function isIconToken(icon: React.ReactNode): icon is RegularHeroIconToken {
  return (
    typeof icon === "string" &&
    (regularHeroIconTokens as readonly string[]).includes(icon)
  );
}

function normalizeImageSource(
  source?: RegularHeroImageSource | false
): NormalizedImageSource | undefined {
  if (!source) {
    return undefined;
  }

  if (typeof source === "string") {
    return { src: source };
  }

  const src = source.src || source.url || source.original || source.thumbnail;

  if (!src) {
    return undefined;
  }

  return {
    src,
    alt: source.alt,
    title: source.title,
    width: source.width,
    height: source.height,
    loading: source.loading,
    decorative: source.decorative,
    position: source.position,
  };
}

function normalizeImageDescriptor(
  image?: RegularHeroImage | false
): (NormalizedImageSource & {
  desktop?: RegularHeroImageSource;
  tablet?: RegularHeroImageSource;
  mobile?: RegularHeroImageSource;
}) | undefined {
  if (!image) {
    return undefined;
  }

  if (typeof image === "string") {
    return { src: image };
  }

  return {
    src: image.src || image.url || image.original || image.thumbnail,
    alt: image.alt,
    title: image.title,
    width: image.width,
    height: image.height,
    loading: image.loading,
    decorative: image.decorative,
    position: image.position,
    desktop: image.desktop,
    tablet: image.tablet,
    mobile: image.mobile,
  };
}

function resolveImage({
  image,
  desktopImage,
  tabletImage,
  mobileImage,
  imagePosition,
  tabletImagePosition,
  mobileImagePosition,
  fallbackAlt,
}: {
  image?: RegularHeroImage | false;
  desktopImage?: RegularHeroImageSource;
  tabletImage?: RegularHeroImageSource;
  mobileImage?: RegularHeroImageSource;
  imagePosition?: string;
  tabletImagePosition?: string;
  mobileImagePosition?: string;
  fallbackAlt?: string;
}): ResolvedImage | undefined {
  const descriptor = normalizeImageDescriptor(image);
  const desktop =
    normalizeImageSource(desktopImage) ||
    normalizeImageSource(descriptor?.desktop) ||
    normalizeImageSource(descriptor);

  if (!desktop?.src) {
    return undefined;
  }

  const tablet =
    normalizeImageSource(tabletImage) ||
    normalizeImageSource(descriptor?.tablet);
  const mobile =
    normalizeImageSource(mobileImage) ||
    normalizeImageSource(descriptor?.mobile);

  return {
    desktop,
    tablet,
    mobile,
    alt: desktop.alt || descriptor?.alt || fallbackAlt || "",
    title: desktop.title || descriptor?.title,
    width: desktop.width || descriptor?.width,
    height: desktop.height || descriptor?.height,
    loading: desktop.loading || descriptor?.loading || "eager",
    decorative: descriptor?.decorative ?? desktop.decorative ?? true,
    desktopPosition:
      imagePosition || desktop.position || descriptor?.position || "center",
    tabletPosition:
      tabletImagePosition ||
      tablet?.position ||
      imagePosition ||
      desktop.position ||
      descriptor?.position ||
      "center",
    mobilePosition:
      mobileImagePosition ||
      mobile?.position ||
      tabletImagePosition ||
      tablet?.position ||
      imagePosition ||
      desktop.position ||
      descriptor?.position ||
      "center",
  };
}

function renderHighlightedString(text: string, highlights: string[] | undefined) {
  const normalizedText = normalizeLineBreaks(text);
  const usableHighlights = (highlights || []).filter(Boolean);
  const lineBreakMatcher = /(\n)/g;

  if (!usableHighlights.length) {
    return normalizedText.split(lineBreakMatcher).map((part, index) => {
      if (part === "\n") {
        return <br key={`line-break-${index}`} />;
      }

      return part;
    });
  }

  const pattern = usableHighlights.map(escapeRegExp).join("|");
  const matcher = new RegExp(`(${pattern}|\n)`, "gi");
  const highlightSet = new Set(usableHighlights.map((item) => item.toLowerCase()));

  return normalizedText.split(matcher).map((part, index) => {
    if (part === "\n") {
      return <br key={`line-break-${index}`} />;
    }

    if (highlightSet.has(part.toLowerCase())) {
      return (
        <span className="regular-hero-title-mark" key={`${part}-${index}`}>
          {part}
        </span>
      );
    }

    return part;
  });
}

function TokenIcon({
  icon,
  size = 22,
  strokeWidth = 2.2,
}: {
  icon?: RegularHeroIcon;
  size?: number;
  strokeWidth?: number;
}) {
  if (!icon) {
    return null;
  }

  if (isIconToken(icon)) {
    if (icon === "google") {
      return (
        <span className="regular-hero-google-icon" aria-hidden="true">
          G
        </span>
      );
    }

    const Icon = iconRegistry[icon];

    return Icon ? (
      <Icon size={size} strokeWidth={strokeWidth} aria-hidden="true" />
    ) : null;
  }

  return <>{icon}</>;
}

function StarRating({ count }: { count?: number }) {
  if (count === undefined || count === null) {
    return null;
  }

  const starCount = Math.max(0, Math.min(5, Math.round(count)));

  if (!starCount) {
    return null;
  }

  return (
    <span className="regular-hero-stars" aria-hidden="true">
      {Array.from({ length: starCount }).map((_, index) => (
        <Star fill="currentColor" key={index} size={18} strokeWidth={0} />
      ))}
    </span>
  );
}

function ActionLink({
  action,
  fallbackVariant,
  fallbackIcon,
  Link,
}: {
  action: RegularHeroAction;
  fallbackVariant: RegularHeroActionVariant;
  fallbackIcon: RegularHeroIcon;
  Link?: RegularHeroLinkComponent;
}) {
  const {
    label,
    mobileLabel,
    description,
    href,
    ariaLabel,
    title,
    target,
    rel,
    icon = fallbackIcon,
    iconPosition = "end",
  } = action;
  const variant = action.outline ? "outline" : action.variant || fallbackVariant;
  const safeRel = target === "_blank" && !rel ? "noreferrer" : rel;
  const className = "regular-hero-action";
  const content = (
    <>
      {icon ? (
        <span className="regular-hero-action-icon" aria-hidden="true">
          <TokenIcon icon={icon} size={22} strokeWidth={2.4} />
        </span>
      ) : null}
      <span className="regular-hero-action-copy">
        {mobileLabel ? (
          <span className="regular-hero-action-label-mobile">{mobileLabel}</span>
        ) : null}
        <span className="regular-hero-action-label">{label}</span>
        {description ? (
          <span className="regular-hero-action-description">{description}</span>
        ) : null}
      </span>
    </>
  );

  if (Link) {
    return (
      <Link
        href={href}
        className={className}
        aria-label={ariaLabel}
        title={title}
        target={target}
        rel={safeRel}
        data-variant={variant}
        data-icon-position={iconPosition}
      >
        {content}
      </Link>
    );
  }

  return (
    <a
      href={href}
      className={className}
      aria-label={ariaLabel}
      title={title}
      target={target}
      rel={safeRel}
      data-variant={variant}
      data-icon-position={iconPosition}
    >
      {content}
    </a>
  );
}

function TrustItem({
  item,
  index,
  Link,
}: {
  item: RegularHeroTrustItem;
  index: number;
  Link?: RegularHeroLinkComponent;
}) {
  const icon = item.icon === undefined ? "check" : item.icon;
  const title = item.title ?? item.label;
  const safeRel = item.target === "_blank" && !item.rel ? "noreferrer" : item.rel;
  const content = (
    <>
      {icon ? (
        <span className="regular-hero-trust-icon" aria-hidden="true">
          <TokenIcon icon={icon} size={34} strokeWidth={2.1} />
        </span>
      ) : null}
      <span className="regular-hero-trust-copy">
        <span className="regular-hero-trust-title-row">
          {title ? <span className="regular-hero-trust-title">{title}</span> : null}
          <StarRating count={item.stars} />
        </span>
        {item.text ? (
          <span className="regular-hero-trust-text">{item.text}</span>
        ) : null}
      </span>
    </>
  );

  if (item.href && Link) {
    return (
      <Link
        href={item.href}
        className="regular-hero-trust-item"
        aria-label={item.ariaLabel}
        target={item.target}
        rel={safeRel}
      >
        {content}
      </Link>
    );
  }

  if (item.href) {
    return (
      <a
        href={item.href}
        className="regular-hero-trust-item"
        aria-label={item.ariaLabel}
        target={item.target}
        rel={safeRel}
      >
        {content}
      </a>
    );
  }

  return (
    <div className="regular-hero-trust-item" key={item.id ?? index}>
      {content}
    </div>
  );
}

function ResponsiveHeroPicture({
  image,
  fallbackAlt,
}: {
  image: ResolvedImage;
  fallbackAlt?: string;
}) {
  const pictureStyle: CssVarStyle = {
    "--regular-hero-image-position": image.desktopPosition,
    "--regular-hero-tablet-image-position": image.tabletPosition,
    "--regular-hero-mobile-image-position": image.mobilePosition,
  };
  const alt = image.decorative ? "" : image.alt || fallbackAlt || "";

  return (
    <picture className="regular-hero-picture" style={pictureStyle}>
      {image.mobile?.src ? (
        <source media="(max-width: 520px)" srcSet={image.mobile.src} />
      ) : null}
      {image.tablet?.src ? (
        <source media="(max-width: 860px)" srcSet={image.tablet.src} />
      ) : null}
      <img
        src={image.desktop.src}
        alt={alt}
        aria-hidden={image.decorative ? "true" : undefined}
        width={image.width}
        height={image.height}
        loading={image.loading}
        decoding="async"
        title={image.title}
      />
    </picture>
  );
}

function RegularHeroBase({
  variant,
  Link,
  theme,
  mode,
  image,
  desktopImage,
  tabletImage,
  mobileImage,
  imagePosition,
  tabletImagePosition,
  mobileImagePosition,
  eyebrow = t.defaults.eyebrow,
  eyebrowIcon,
  eyebrowVariant = "plain",
  title = t.defaults.title,
  titleHighlight = t.defaults.titleHighlight,
  titleHighlights,
  titleHighlightPlacement = "block",
  titleComponent = "h1",
  lead = t.defaults.lead,
  primaryAction = t.defaults.primaryAction as RegularHeroAction,
  secondaryAction = t.defaults.secondaryAction as RegularHeroAction,
  trustItems,
  trustCtas,
  colors,
  colours,
  accentColor,
  accentColour,
  highlightColor,
  highlightColour,
  backgroundColor,
  backgroundColour,
  surfaceColor,
  surfaceColour,
  textColor,
  textColour,
  textSoftColor,
  textSoftColour,
  maxWidth = t.sizes.maxWidth,
  minHeight = t.sizes.minHeight,
  mediaAspectRatio = t.sizes.mediaAspectRatio,
  id,
  className = "",
  style,
  ariaLabel,
}: RegularHeroBaseProps) {
  const resolvedTheme = mode || theme || (t.defaults.theme as RegularHeroTheme);
  const HeadingTag = titleComponent;
  const safeId = sanitizeId(id, variant === "split" ? "regular-split-hero" : "regular-hero");
  const titleId = `${safeId}-title`;
  const plainTitle = typeof title === "string" ? title.replace(/\s+/g, " ").trim() : undefined;
  const accessibleLabel = ariaLabel || plainTitle;
  const hasTitle = Boolean(title || titleHighlight);
  const mergedColors = {
    ...t.colors,
    ...colours,
    ...colors,
  };
  const resolvedTrustItems =
    trustCtas || trustItems || ([...t.defaults.trustItems] as RegularHeroTrustItem[]);
  const resolvedImage = resolveImage({
    image,
    desktopImage,
    tabletImage,
    mobileImage,
    imagePosition,
    tabletImagePosition,
    mobileImagePosition,
    fallbackAlt: plainTitle,
  });
  const sectionStyle: CssVarStyle = {
    "--regular-hero-bg":
      backgroundColor ||
      backgroundColour ||
      (resolvedTheme === "dark"
        ? mergedColors.darkBackground
        : mergedColors.lightBackground),
    "--regular-hero-surface":
      surfaceColor ||
      surfaceColour ||
      (resolvedTheme === "dark" ? mergedColors.darkSurface : mergedColors.lightSurface),
    "--regular-hero-text":
      textColor ||
      textColour ||
      (resolvedTheme === "dark" ? mergedColors.darkText : mergedColors.lightText),
    "--regular-hero-text-soft":
      textSoftColor ||
      textSoftColour ||
      (resolvedTheme === "dark" ? mergedColors.darkTextSoft : mergedColors.lightTextSoft),
    "--regular-hero-text-muted":
      resolvedTheme === "dark" ? mergedColors.darkTextMuted : mergedColors.lightTextMuted,
    "--regular-hero-border":
      resolvedTheme === "dark" ? mergedColors.darkBorder : mergedColors.lightBorder,
    "--regular-hero-overlay":
      resolvedTheme === "dark" ? mergedColors.darkOverlay : mergedColors.lightOverlay,
    "--regular-hero-accent": accentColor || accentColour || mergedColors.accent,
    "--regular-hero-accent-dark": mergedColors.accentDark,
    "--regular-hero-accent-contrast": mergedColors.accentContrast,
    "--regular-hero-highlight":
      highlightColor || highlightColour || accentColor || accentColour || mergedColors.highlight,
    "--regular-hero-shadow": mergedColors.shadow,
    "--regular-hero-max-width": getCssSize(maxWidth),
    "--regular-hero-min-height": getCssSize(minHeight),
    "--regular-hero-media-ratio": mediaAspectRatio,
    ...style,
  };

  return (
    <section
      className={`regular-hero ${className}`.trim()}
      data-component={variant === "split" ? "regular-split-hero" : "regular-hero"}
      data-theme={resolvedTheme}
      data-variant={variant}
      data-has-image={resolvedImage ? "true" : "false"}
      data-frame={variant === "split" ? "true" : "false"}
      id={safeId}
      aria-labelledby={hasTitle ? titleId : undefined}
      aria-label={!hasTitle ? accessibleLabel : undefined}
      style={sectionStyle}
    >
      <style>{regularHeroCss}</style>

      <div className="regular-hero-shell">
        <div className="regular-hero-grid">
          <div className="regular-hero-content">
            {eyebrow ? (
              <div className="regular-hero-eyebrow" data-variant={eyebrowVariant}>
                {eyebrowIcon ? (
                  <span className="regular-hero-eyebrow-icon" aria-hidden="true">
                    <TokenIcon icon={eyebrowIcon} size={20} strokeWidth={2.4} />
                  </span>
                ) : null}
                <span className="regular-hero-eyebrow-text">{eyebrow}</span>
              </div>
            ) : null}

            {hasTitle ? (
              <HeadingTag className="regular-hero-title" id={titleId}>
                {title ? (
                  <span className="regular-hero-title-main">
                    {typeof title === "string"
                      ? renderHighlightedString(title, titleHighlights)
                      : title}
                  </span>
                ) : null}
                {titleHighlight ? (
                  <>
                    {titleHighlightPlacement === "inline" ? " " : null}
                    <span
                      className="regular-hero-title-highlight"
                      data-placement={titleHighlightPlacement}
                    >
                      {typeof titleHighlight === "string"
                        ? renderHighlightedString(titleHighlight, undefined)
                        : titleHighlight}
                    </span>
                  </>
                ) : null}
              </HeadingTag>
            ) : null}

            {lead ? <p className="regular-hero-lead">{lead}</p> : null}

            {primaryAction || secondaryAction ? (
              <div className="regular-hero-actions">
                {primaryAction ? (
                  <ActionLink
                    action={primaryAction}
                    fallbackIcon="arrowRight"
                    fallbackVariant="solid"
                    Link={Link}
                  />
                ) : null}
                {secondaryAction ? (
                  <ActionLink
                    action={secondaryAction}
                    fallbackIcon="arrowRight"
                    fallbackVariant="outline"
                    Link={Link}
                  />
                ) : null}
              </div>
            ) : null}

            {resolvedTrustItems.length ? (
              <div className="regular-hero-trust" aria-label="Trust signals">
                {resolvedTrustItems.map((item, index) => (
                  <TrustItem
                    item={item}
                    index={index}
                    key={item.id ?? index}
                    Link={Link}
                  />
                ))}
              </div>
            ) : null}
          </div>

          {resolvedImage ? (
            <div className="regular-hero-media-wrap">
              <ResponsiveHeroPicture image={resolvedImage} fallbackAlt={plainTitle} />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default function RegularHero(props: RegularHeroProps) {
  return <RegularHeroBase {...props} variant="canvas" />;
}

export function RegularSplitHero(props: RegularHeroProps) {
  return <RegularHeroBase {...props} variant="split" />;
}

export { regularHeroIconTokens, regularHeroTokens };
export type { RegularHeroColorToken, RegularHeroIconToken };
